import { createListenerMiddleware } from '@reduxjs/toolkit';

import getReflectContext from 'editor/src/util/reflectDesignData/getReflectContext';

import getFirstGridElement from './design/selector/getFirstGridElement';
import { addCalendarPublicEventsAction, setDesignDataFromVariantSwitchAction } from './design/slice';
import getNewDesignDataForGroup from './variants/helpers/getNewDesignDataForGroup';
import getVariationGroupsFromDesignOptions from './variants/selector/getVariationGroupsFromDesignOptions';
import { setGroupDesignDataAction } from './variants/slice';
import checkPluginUse from './watchers/checkPluginUse';

import type { ThunkDispatch } from './hooks';
import type { RootState, ThunkArg } from './index';
import type { i18n } from 'i18next';

function createListener(i18n: i18n) {
  const listenerMiddleware = createListenerMiddleware<RootState, ThunkDispatch, ThunkArg>({ extra: { i18n } });

  listenerMiddleware.startListening({
    predicate: (action, currentState, previousState) =>
      !!action.type &&
      (currentState.design.designData !== previousState.design.designData ||
        currentState.design.globalPlugins !== previousState.design.globalPlugins),
    effect: (action, listenerApi) => {
      let state = listenerApi.getState();
      const { designData: originalDesignData } = listenerApi.getOriginalState().design;

      const isVariantSwitch = action.type === setDesignDataFromVariantSwitchAction.type;

      // Skip checkPluginUse when triggered by setInstalledPluginsAction — plugins were just computed and
      // are already correct, so re-running would be a no-op. We still continue past this block so that
      // the variant reflection below propagates the updated plugins into variationGroups and triggers
      // the postMessage to the host.
      if (action.type !== 'design/setInstalledPluginsAction' && state.design.designData) {
        checkPluginUse(
          state.design.designData,
          state.gallery.images,
          listenerApi.dispatch,
          state.plugins.list,
          state.design.globalPlugins,
          state.hostSettings.defaultPersonalizationPlugin,
        );
      }

      state = listenerApi.getState();
      const { designData } = state.design;

      // Holidays can arrive before the design, and the host may then replace the design with one
      // saved without them (the product wizard sets it twice), so re-apply them on either.
      const designLacksPublicHolidays =
        designData !== originalDesignData &&
        !!getFirstGridElement(state) &&
        !designData?.calendar?.events?.some(({ custom, merchant }) => !custom && !merchant);
      if (designData && state.gridDesigns.gridEvents.length && (!originalDesignData || designLacksPublicHolidays)) {
        listenerApi.dispatch(addCalendarPublicEventsAction(state.gridDesigns.gridEvents));
      }

      if (state.variants.isVariantFlow && designData) {
        const reflectContext = getReflectContext(state);
        const { selectedGroupKey, variationGroups, selectedDesignOptionValue } = state.variants;
        const variationGroupsFromDesignOptions = getVariationGroupsFromDesignOptions(state);
        const selectedGroupDesignOption = variationGroupsFromDesignOptions?.find(
          (group) => group.key === selectedDesignOptionValue,
        );
        const selectedGroupKeys = selectedGroupDesignOption?.value.map((group) => group.key);
        const selectedGroup = variationGroups.find((group) => group.key === selectedGroupKey);

        if (!selectedGroup) {
          return;
        }
        if (!selectedGroup.linked) {
          listenerApi.dispatch(
            setGroupDesignDataAction({
              groupKey: selectedGroup.key,
              designData,
              reflectContext,
            }),
          );
          return;
        }

        if (isVariantSwitch) {
          listenerApi.dispatch(
            setGroupDesignDataAction({
              groupKey: selectedGroup.key,
              designData,
              reflectContext,
            }),
          );
          return;
        }
        variationGroups.forEach((group) => {
          if (
            (selectedGroupKeys && selectedGroupKeys.find((selectedkey) => selectedkey === group.key) && group.linked) ||
            (!selectedGroupKeys && group.linked)
          ) {
            const groupDesignUpdate = getNewDesignDataForGroup(group, state, listenerApi.extra.i18n);
            listenerApi.dispatch(
              setGroupDesignDataAction({
                groupKey: group.key,
                designData: groupDesignUpdate.designData,
                reflectContext,
                // GAPI-47583 — the group cannot follow this design edit until its
                // template arrives: flag its designData as out of date so the
                // export watcher does not present it to the host as current.
                templatesMissing: groupDesignUpdate.missingTemplate,
              }),
            );
          }
        });
      }
    },
  });

  return listenerMiddleware;
}

export default createListener;
